/**
 * RouteErrorBoundary — wraps Outlet to catch render errors. Renders an
 * editorial fallback (serif heading + retry CTA).
 *
 * Per Epic 9: every route is wrapped so a render-time error does not blank
 * the whole shell.
 */
import * as React from 'react';
import { Button } from './ui/Button';

interface State {
  hasError: boolean;
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
}

export class RouteErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Editorial silence — we don't surface stack traces. Log to the console
    // for diagnosis in dev.
    if (typeof console !== 'undefined' && console.error) {
      // eslint-disable-next-line no-console
      console.error('[RouteErrorBoundary]', error, info.componentStack);
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <section className="mx-auto max-w-md text-center py-20 md:py-24 px-6">
          <h2 className="font-serif text-3xl text-ink-900 leading-tight">
            Something didn&apos;t render.
          </h2>
          <p className="mt-4 font-sans text-sm text-ink-500 leading-relaxed">
            We&apos;ve noted it. Refresh, or try again in a moment.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Button variant="primary" onClick={this.reset}>
              Try again
            </Button>
            <Button variant="ghost" asChild>
              <a href="/">Back to your journal</a>
            </Button>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}
